/**
 * THE share implementation. One composite builder, one delivery path, three call
 * sites (`src/components/user-app/UserHome.tsx`,
 * `src/pages/consumer/S5Reveal.tsx`, `src/pages/consumer/S9bStanding.tsx`).
 * Do NOT re-implement either half in a component — extend this module instead.
 *
 * COMPOSITE (1080×1350 portrait) — the card and a wordmark, nothing else:
 *   1. THE CARD — rasterised from the SAME designed-card node the funnel renders
 *      on screen, scaled as large as minimal margins allow.
 *   2. A SMALL "cardmoji" wordmark, centred beneath the card.
 *
 * WHY the wordmark survives and the type didn't: the share TEXT already carries
 * the tagline, the CTA and the link, so painting them into the image duplicated
 * the message beside itself. The wordmark is not marketing — it is attribution
 * for the case the image travels WITHOUT its text (saved to camera roll,
 * re-shared, posted to a story). So it is deliberately discreet: small, Ink on
 * the Paper ground, never competing with the card.
 *
 * Deliberately absent: the tagline, the CTA line, the paint-palette and
 * stock-debit-card decorations, the emoji accent and the referral-code badge.
 * The `tagline`, `ctaLine`, `headline`, `accent` and `code` fields are kept only
 * so existing call sites compile; they are NOT painted.
 *
 * `SHARE_TAGLINE` and `shareCtaLine()` are still exported — they belong to the
 * share TEXT, which is unchanged. They are simply no longer drawn on the canvas.
 *
 * Delivery, in order of preference:
 *   1. navigator.share({ files }) — image inline in the OS sheet (mobile).
 *   2. navigator.share({ text, url }) — link-only sheet where files are refused.
 *   3. DESKTOP (no navigator.share): download the PNG *and* copy the link, and
 *      return "downloaded_copied" so the caller can show a persistent
 *      confirmation. Never a silent clipboard write.
 */
import { toPng } from "html-to-image";
import * as Sentry from "@sentry/react";

/** Design-system brand constants. Ink / Paper / Grape. */
const INK = "#1C2030";
const PAPER = "#F7F5F1";
/** Kept: Grape is the alternate wordmark ink if the ground ever changes. */
const GRAPE = "#8834B2";
void GRAPE;
const BRAND_FACE = '"Clash Display", "Space Grotesk", system-ui, -apple-system, sans-serif';

/** Belongs to the share TEXT, not the image. */
export const SHARE_TAGLINE = "Your face. Your card.";

/** The CTA line for the share TEXT. Uses the LIVE host, never a hardcoded domain. */
export function shareCtaLine(host?: string): string {
  const h = (host || (typeof window !== "undefined" ? window.location.host : "") || "cardmoji.com")
    .replace(/^www\./, "");
  return `Make your own at ${h}`;
}

/** The only string painted into the composite. */
export const SHARE_WORDMARK = "cardmoji";

export interface ShareCompositeSpec {
  /**
   * The CARD node to rasterise — the designed-card face itself, not a framed
   * mock-up. Whatever is inside this node is what recipients see, so it must be
   * the same render the user is previewing.
   */
  node: HTMLElement;
  /** Kept for API compatibility; the ground is Paper, not the card colour. */
  colourFrom?: string;
  colourTo?: string;
  /** DEPRECATED / IGNORED — tagline lives in the share text, not the image. */
  tagline?: string;
  /** DEPRECATED / IGNORED — CTA lives in the share text, not the image. */
  ctaLine?: string;
  /** Used only to name the downloaded file. */
  brandLine: string;
  /** DEPRECATED / IGNORED — headline block removed. */
  headline?: string[];
  /** DEPRECATED / IGNORED — no code badge; attribution rides in the URL. */
  code?: string | null;
  /** DEPRECATED / IGNORED — emoji accent line removed. */
  accent?: string;
  fileName?: string;
}

export type ShareOutcome =
  | "shared"
  | "shared_link"
  | "downloaded_copied"
  | "copied"
  | "cancelled"
  | "failed";

const W = 1080;
const H = 1350;

/** Composite geometry, shared with S5's on-screen preview frame so the two
 *  cannot drift. Fractions of the 1080×1350 frame. */
export const SHARE_FRAME = {
  aspect: W / H,
  sideMargin: 64 / W,
  topMargin: 64 / H,
  bottomMargin: 56 / H,
  wordmarkPx: 34 / H,
  gapCardToWordmark: 40 / H,
} as const;

function safeFileName(name: string): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return `${slug || "cardmoji"}-card.png`;
}

/**
 * Wrap-first fitter. Long strings WRAP to at most `maxLines`; the size only
 * steps down once wrapping has been tried, and never below `minPx` — a shrunken
 * unreadable line is worse than a second line. Currently used by the wordmark
 * (which never wraps in practice) and kept as the module's one text fitter so
 * any line added later inherits the rule rather than re-deriving it.
 */
export function fitWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  startPx: number,
  weight: string,
  minPx = 30,
  maxLines = 2,
): { px: number; lines: string[] } {
  const words = text.split(/\s+/).filter(Boolean);
  const layout = (px: number): string[] | null => {
    ctx.font = `${weight} ${px}px ${BRAND_FACE}`;
    const lines: string[] = [];
    let line = "";
    for (const word of words) {
      const next = line ? `${line} ${word}` : word;
      if (ctx.measureText(next).width <= maxWidth || !line) {
        line = next;
      } else {
        lines.push(line);
        line = word;
      }
    }
    if (line) lines.push(line);
    if (lines.length > maxLines) return null;
    if (lines.some((l) => ctx.measureText(l).width > maxWidth)) return null;
    return lines;
  };

  let px = startPx;
  for (;;) {
    const lines = layout(px);
    if (lines) {
      ctx.font = `${weight} ${px}px ${BRAND_FACE}`;
      return { px, lines };
    }
    if (px <= minPx) break;
    px = Math.max(minPx, px - 2);
  }
  ctx.font = `${weight} ${px}px ${BRAND_FACE}`;
  return { px, lines: layout(px) || [text] };
}

/** Rasterise + composite. Returns null when the node can't be captured. */
export async function buildShareImageFile(spec: ShareCompositeSpec): Promise<File | null> {
  try {
    // Brand face must be resolved before measuring, or the fallback metrics
    // decide the layout and the real face overflows it.
    try {
      await (document as any).fonts?.load?.(`600 72px "Clash Display"`);
      await (document as any).fonts?.ready;
    } catch {
      /* non-fatal: the fallback stack still measures correctly */
    }

    // Images need the SAME treatment as the font: html-to-image rasterises
    // whatever is decoded at capture time, so an image still in flight (or one
    // that never started, as happens to a lazy image in an off-screen node)
    // renders as nothing and the card arrives without its avatar. Await every
    // <img> in the subtree before measuring or rasterising.
    await Promise.all(
      Array.from(spec.node.querySelectorAll("img")).map(async (img) => {
        try {
          if (!img.complete || img.naturalWidth === 0) {
            await new Promise<void>((res) => {
              const done = () => res();
              img.addEventListener("load", done, { once: true });
              img.addEventListener("error", done, { once: true });
              setTimeout(done, 8000);
            });
          }
          await (img as any).decode?.();
        } catch {
          /* non-fatal: a broken image must not block the whole composite */
        }
      }),
    );

    const cardDataUrl = await toPng(spec.node, { pixelRatio: 2, cacheBust: true });

    const cardImg = new Image();
    cardImg.crossOrigin = "anonymous";
    await new Promise<void>((res, rej) => {
      cardImg.onload = () => res();
      cardImg.onerror = rej;
      cardImg.src = cardDataUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d")!;

    ctx.fillStyle = PAPER;
    ctx.fillRect(0, 0, W, H);

    // ---- Wordmark: measured first, but it claims almost nothing ----
    const sideMargin = SHARE_FRAME.sideMargin * W;
    const topMargin = SHARE_FRAME.topMargin * H;
    const bottomMargin = SHARE_FRAME.bottomMargin * H;
    const gap = SHARE_FRAME.gapCardToWordmark * H;
    const { px: markPx, lines: markLines } = fitWrappedText(
      ctx,
      SHARE_WORDMARK,
      W - sideMargin * 2,
      SHARE_FRAME.wordmarkPx * H,
      "600",
      30,
      1,
    );
    const markBlockH = markPx * markLines.length;

    // ---- The card: everything the frame has left ----
    const maxCardW = W - sideMargin * 2;
    const maxCardH = H - topMargin - gap - markBlockH - bottomMargin;
    const scale = Math.min(maxCardW / cardImg.width, maxCardH / cardImg.height);
    const cw = cardImg.width * scale;
    const ch = cardImg.height * scale;
    const cx = (W - cw) / 2;
    // A landscape card is short and wide and can never fill the tall slot, so
    // the card + wordmark travel as ONE group centred vertically. Otherwise a
    // landscape share leaves a dead band under the card while the composition
    // sags toward the bottom.
    const availH = H - topMargin - bottomMargin;
    const groupH = ch + gap + markBlockH;
    const cy = topMargin + Math.max(0, (availH - groupH) / 2);

    ctx.save();
    ctx.shadowColor = "rgba(28,32,48,0.28)";
    ctx.shadowBlur = 56;
    ctx.shadowOffsetY = 24;
    ctx.drawImage(cardImg, cx, cy, cw, ch);
    ctx.restore();

    // Wordmark hangs off the ACTUAL card bottom, so the gap under the card is
    // identical in portrait and landscape.
    ctx.textAlign = "center";
    ctx.fillStyle = INK;
    ctx.font = `600 ${markPx}px ${BRAND_FACE}`;
    let baseline = cy + ch + gap + markPx * 0.8;
    for (const line of markLines) {
      ctx.fillText(line, W / 2, baseline);
      baseline += markPx;
    }

    const blob = await new Promise<Blob>((res) => canvas.toBlob((b) => res(b!), "image/png"));
    return new File([blob], spec.fileName || safeFileName(spec.brandLine), { type: "image/png" });
  } catch {
    return null;
  }
}


/** Force a browser download of a generated File. */
export function downloadFile(file: File) {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export type ShareChannel =
  | "os"
  | "Instagram"
  | "TikTok"
  | "WhatsApp"
  | "X"
  | "Messages"
  | "Save";

/**
 * Per-channel web intents, used only where the OS sheet is the wrong fit.
 * Instagram/TikTok have no web composer that accepts an image, so those stay on
 * the sheet (mobile) or the download+copy path (desktop).
 */
export function channelIntentUrl(channel: ShareChannel, text: string, url: string): string | null {
  const body = `${text}\n${url}`;
  switch (channel) {
    case "WhatsApp":
      return `https://wa.me/?text=${encodeURIComponent(body)}`;
    case "X":
      return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    case "Messages":
      return `sms:?&body=${encodeURIComponent(body)}`;
    default:
      return null;
  }
}

export interface ShareCardInput {
  composite: ShareCompositeSpec | null;
  /**
   * A composite built AHEAD of the tap (see prepareShareImage). When present it
   * is used instead of rasterising inside the tap handler. This is the fix for
   * the "shares a link / downloads instead" regression: navigator.share() must
   * run while the tap's transient activation is still valid (~5 s on Chrome,
   * less on Safari). Font loading + image decode + html-to-image + an edge
   * function call inside the handler blew that window on real phones, share()
   * rejected with NotAllowedError, and the code fell through to the link /
   * download path with no trace.
   */
  preparedFile?: Promise<File | null> | File | null;
  shareUrl: string;
  /** Caption / share text. The url is appended for link-only paths. */
  text: string;
  title?: string;
  channel?: ShareChannel;
}

/** Reason the last shareCard() call did NOT deliver the image inline. */
export let lastShareFallbackReason: string | null = null;

/**
 * A silent degradation must be COUNTABLE in our own system, not only written to
 * a log line nobody reads. Three destinations, deliberately:
 *   1. the console (developer, immediate),
 *   2. Sentry (aggregated client errors),
 *   3. `record_share_fallback` → `v_share_image_fallbacks`, which the nightly
 *      invariant sweep PAGES on. That is the one that survives nobody looking.
 * Carries no identifiers — a reason and a channel only.
 */
function noteFallback(reason: string, err?: unknown, channel?: ShareChannel) {
  lastShareFallbackReason = reason;
  console.warn(`[share-card] image not delivered inline: ${reason}`, err ?? "");
  try {
    Sentry.captureMessage(`share-card fallback: ${reason}`, {
      level: "warning",
      extra: { error: err instanceof Error ? `${err.name}: ${err.message}` : String(err ?? "") },
    });
  } catch {
    /* Sentry not initialised */
  }
  // Fire-and-forget: telemetry must never delay or break the share itself.
  void (async () => {
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      await supabase.rpc("record_share_fallback", {
        _reason: reason,
        _channel: channel ?? "os",
      });
    } catch {
      /* offline or blocked: Sentry + console still hold the signal */
    }
  })();
}


/**
 * Build the composite BEFORE the user taps, so the tap handler only has to call
 * navigator.share(). Dedupes on (node, key): call it on mount and again when the
 * card changes; the same promise is returned until the key changes.
 *
 * STALENESS: one entry per node, replaced whenever the key changes, so the tap
 * can only ever read the LATEST card's build — a superseded build is dropped, it
 * is never attached. Rapid changes are coalesced by the caller (debounce), which
 * also stops N concurrent rasterisations from fighting for the main thread.
 */
type PreparedEntry = { key: string; promise: Promise<File | null>; settled: boolean };
const preparedByNode = new WeakMap<HTMLElement, PreparedEntry>();
export function prepareShareImage(spec: ShareCompositeSpec, key: string): Promise<File | null> {
  const hit = preparedByNode.get(spec.node);
  if (hit && hit.key === key) return hit.promise;
  const promise = buildShareImageFile(spec);
  const entry: PreparedEntry = { key, promise, settled: false };
  preparedByNode.set(spec.node, entry);
  void promise.then(
    () => {
      entry.settled = true;
    },
    () => {
      entry.settled = true;
    },
  );
  return promise;
}

/** True when the prepared composite for this node has already finished building. */
export function isShareImageReady(node: HTMLElement | null): boolean {
  return !!node && !!preparedByNode.get(node)?.settled;
}

/**
 * Deliver the share. Always resolves — callers branch on the outcome to show a
 * visible confirmation.
 */
export async function shareCard(input: ShareCardInput): Promise<ShareOutcome> {
  const { shareUrl, text, title = "cardmoji", channel = "os" } = input;
  lastShareFallbackReason = null;
  let file: File | null = null;
  if (input.preparedFile !== undefined && input.preparedFile !== null) {
    // If the build has NOT settled yet, awaiting it here spends the tap's
    // transient-activation window — the exact failure this whole path exists to
    // avoid. It is still the best available option (a link with no image is
    // worse), but it must be counted so a slow device shows up as a pattern.
    if (input.composite && !isShareImageReady(input.composite.node)) {
      noteFallback("prepared composite still building at tap", undefined, channel);
    }
    file = await input.preparedFile;
    if (!file) noteFallback("prepared composite was null", undefined, channel);
  }
  if (!file && input.composite) {
    file = await buildShareImageFile(input.composite);
    if (!file) noteFallback("composite build failed", undefined, channel);
  }


  // Explicit save: download the PNG, nothing else.
  if (channel === "Save") {
    if (!file) return "failed";
    downloadFile(file);
    return "downloaded_copied";
  }

  const canShareFiles = !!file && !!navigator.canShare?.({ files: [file] });
  if (file && !canShareFiles && typeof navigator.share === "function") {
    noteFallback("navigator.canShare({files}) returned false", undefined, channel);
  }

  if (typeof navigator.share === "function") {
    try {
      if (canShareFiles) {
        await navigator.share({ title, text: `${text}\n${shareUrl}`, files: [file!] });
        return "shared";
      }
      const intent = channelIntentUrl(channel, text, shareUrl);
      if (intent) {
        window.open(intent, "_blank", "noopener");
        return "shared_link";
      }
      await navigator.share({ title, text, url: shareUrl });
      return "shared_link";
    } catch (err: any) {
      if (err?.name === "AbortError") return "cancelled";
      noteFallback(`navigator.share rejected (${err?.name ?? "unknown"})`, err, channel);
      // fall through to the desktop path
    }
  }

  // DESKTOP: no share sheet. Download the image AND copy the link, so something
  // observable always happens.
  const intent = channelIntentUrl(channel, text, shareUrl);
  if (file) downloadFile(file);
  const copied = await copyText(`${text}\n${shareUrl}`);
  if (intent) window.open(intent, "_blank", "noopener");
  if (file) return "downloaded_copied";
  return copied ? "copied" : "failed";
}
